/**
 * Environment variable access with runtime override support.
 *
 * Supports:
 * 1. Runtime config via window.__env (for containerized deployments)
 * 2. Build-time process.env values
 */

type RuntimeGlobal = typeof globalThis & {
    __env?: Record<string, string>
    location?: {
        protocol?: string
        hostname?: string
        port?: string
    }
}

/** Build an origin string from runtime `window.location` parts, including
 * `port` when present (the browser's `port` field is `""` on default ports
 * 80/443). Returns `undefined` when protocol or hostname is missing. */
const buildRuntimeOrigin = (): string | undefined => {
    const runtimeLocation = (globalThis as RuntimeGlobal).location
    const {protocol, hostname, port} = runtimeLocation ?? {}
    if (!protocol || !hostname) return undefined
    return port ? `${protocol}//${hostname}:${port}` : `${protocol}//${hostname}`
}

// Build-time environment variables
export const processEnv = {
    NEXT_PUBLIC_AGENTA_LICENSE: process.env.NEXT_PUBLIC_AGENTA_LICENSE,
    NEXT_PUBLIC_AGENTA_BILLING_ENABLED: process.env.NEXT_PUBLIC_AGENTA_BILLING_ENABLED,
    NEXT_PUBLIC_AGENTA_TOOLS_ENABLED: process.env.NEXT_PUBLIC_AGENTA_TOOLS_ENABLED,
    NEXT_PUBLIC_AGENTA_MCP_GATEWAY_ENABLED: process.env.NEXT_PUBLIC_AGENTA_MCP_GATEWAY_ENABLED,
    NEXT_PUBLIC_AGENTA_WALLETS_ENABLED: process.env.NEXT_PUBLIC_AGENTA_WALLETS_ENABLED,
    NEXT_PUBLIC_AGENTA_EMAIL_DELIVERY_ENABLED:
        process.env.NEXT_PUBLIC_AGENTA_EMAIL_DELIVERY_ENABLED,
    NEXT_PUBLIC_AGENTA_WEB_URL: process.env.NEXT_PUBLIC_AGENTA_WEB_URL,
    NEXT_PUBLIC_AGENTA_API_URL: process.env.NEXT_PUBLIC_AGENTA_API_URL,
    NEXT_PUBLIC_POSTHOG_API_KEY: process.env.NEXT_PUBLIC_POSTHOG_API_KEY,
    NEXT_PUBLIC_CRISP_WEBSITE_ID: process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID,
    NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY:
        process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY,
    NEXT_PUBLIC_LOG_APP_ATOMS: process.env.NEXT_PUBLIC_LOG_APP_ATOMS,
    NEXT_PUBLIC_ENABLE_ATOM_LOGS: process.env.NEXT_PUBLIC_ENABLE_ATOM_LOGS,
    NEXT_PUBLIC_AGENTA_SANDBOX_LOCAL_ENABLED: process.env.NEXT_PUBLIC_AGENTA_SANDBOX_LOCAL_ENABLED,
    NEXT_PUBLIC_AGENTA_ENABLED_SANDBOX_PROVIDERS:
        process.env.NEXT_PUBLIC_AGENTA_ENABLED_SANDBOX_PROVIDERS,
}

/**
 * Get environment variable value.
 * Checks window.__env first (runtime), falls back to process.env (build-time).
 */
export const getEnv = (envKey: string): string => {
    // Check for runtime config first (browser/worker)
    const runtimeEnv = (globalThis as RuntimeGlobal).__env
    if (runtimeEnv && Object.prototype.hasOwnProperty.call(runtimeEnv, envKey)) {
        return runtimeEnv[envKey]
    }

    // Fall back to build-time environment
    return processEnv[envKey as keyof typeof processEnv] || ""
}

/**
 * Get the Agenta API URL.
 * Falls back to current origin if not configured.
 */
export const getAgentaApiUrl = (): string => {
    const apiUrl = getEnv("NEXT_PUBLIC_AGENTA_API_URL")
    if (apiUrl) return apiUrl
    return buildRuntimeOrigin() ?? ""
}

/**
 * Get the Agenta Web URL.
 * Falls back to current origin if not configured.
 */
export const getAgentaWebUrl = (): string => {
    const webUrl = getEnv("NEXT_PUBLIC_AGENTA_WEB_URL")
    if (webUrl) return webUrl
    return buildRuntimeOrigin() ?? ""
}

/**
 * The spellings that mean on, mirrored from `_TRUTHY` in `api/oss/src/utils/env.py`.
 *
 * One vocabulary for both halves of a deployment. An operator writes a switch once, in one
 * env file both containers read, and has no reason to expect `0` to stop the API and leave
 * the interface running.
 */
const TRUTHY_ENV_VALUES = new Set(["true", "1", "t", "y", "yes", "on", "enable", "enabled"])

/**
 * A boolean deployment switch, parsed exactly as the API's `_parse_bool_env` parses it:
 * unset or blank takes the default, and anything else is on only when it is one of the
 * accepted spellings. So `0`, `off` and `no` turn a flag off here as they do there.
 */
const parseBoolEnv = (key: string, fallback: boolean): boolean => {
    const value = getEnv(key).trim()
    if (!value) return fallback
    return TRUTHY_ENV_VALUES.has(value.toLowerCase())
}

export const isSandboxLocalEnabled = (): boolean =>
    parseBoolEnv("NEXT_PUBLIC_AGENTA_SANDBOX_LOCAL_ENABLED", true)

/** The sandbox providers this deployment enabled, normalized to lowercase ids. Unset/empty
 * falls back to `["local"]` so the picker never hides every option. */
export const getEnabledSandboxProviders = (): string[] => {
    const providers = getEnv("NEXT_PUBLIC_AGENTA_ENABLED_SANDBOX_PROVIDERS")
        .split(",")
        .map((provider) => provider.trim().toLowerCase())
        .filter(Boolean)
    if (providers.length === 0) return ["local"]
    // `inprocess` is enabled wherever `daytona` is (the runner, the SDK, the API and
    // entrypoint.sh apply the same rule). The picker shows it as Daytona: the runner routes by harness.
    if (providers.includes("daytona") && !providers.includes("inprocess")) {
        providers.push("inprocess")
    }
    return providers
}

/**
 * Edition and feature gates, read from the same env on every host.
 *
 * These decide which settings tabs exist, so the desktop and /m have to agree — a second
 * copy of `=== "ee"` somewhere is how one surface ends up showing a tab the other hides.
 */

/** Enterprise or any cloud tier. Note `cloud*` counts: cloud runs the EE feature set. */
export const isEE = (): boolean => {
    const license = getEnv("NEXT_PUBLIC_AGENTA_LICENSE")?.toLowerCase()
    if (!license) return false
    return license === "ee" || license.startsWith("cloud")
}

/** Gates BOTH the Tools and Triggers settings tabs. */
export const isToolsEnabled = (): boolean => getEnv("NEXT_PUBLIC_AGENTA_TOOLS_ENABLED") === "true"

export const isBillingEnabled = (): boolean =>
    getEnv("NEXT_PUBLIC_AGENTA_BILLING_ENABLED") === "true"

/**
 * Whether this deployment serves the MCP gateway, and so whether the MCP endpoints settings
 * tab has anything behind it. The API's `AGENTA_MCP_GATEWAY_ENABLED` is the authority;
 * `entrypoint.sh` mirrors that same variable into `__env.js`, so this is a view of the API's
 * switch and not a second one.
 *
 * On by default, matching the API: an existing deployment sets nothing and keeps the tab,
 * which is the behavior it has today. Off for every spelling the API reads as off, so an
 * operator who writes `0`, `off` or `no` is not left with a settings tab, a connect journey
 * and an agent-config surface whose every request the API answers with a 403.
 */
export const isMcpGatewayEnabled = (): boolean =>
    parseBoolEnv("NEXT_PUBLIC_AGENTA_MCP_GATEWAY_ENABLED", true)

/**
 * Whether the credit wallet is on. The API's `AGENTA_WALLETS_ENABLED` is the authority;
 * `entrypoint.sh` mirrors it into `__env.js`. Off by default, like the API.
 */
export const isWalletsEnabled = (): boolean =>
    parseBoolEnv("NEXT_PUBLIC_AGENTA_WALLETS_ENABLED", false)

export const isEmailInvitationsEnabled = (): boolean =>
    getEnv("NEXT_PUBLIC_AGENTA_EMAIL_DELIVERY_ENABLED") === "true"
